import { useCallback, useEffect, useRef, useState } from 'react';

import { Currencies } from 'constants/currencies';
import { clearHiddenFieldValues } from 'helpers/field-config/form-page-helpers';
import { getCheckTypeIcon } from 'helpers/iconsHelper';
import { useForm } from 'react-hook-form';

import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import { EditableSettings } from 'components/EditableSettings/EditableSettings';
import FieldConfigRenderer from 'components/FieldConfigRenderer/FieldConfigRenderer';
import {
  Button,
  FormMessage,
  Input,
  InputGroup,
  Select,
  ToggleSwitch,
} from 'components/FormComponents';
import { ToggleSwitchInput } from 'components/FormComponents/ToggleSwitchInput/ToggleSwitchInput';
import { IconButton } from 'components/IconButton/IconButton';

function CustomPricingStatement({ billingCurrency, defaultPrice, customPrice, isEnterprise }) {
  const currency = billingCurrency ? String(billingCurrency).toUpperCase() : null;
  const defaultNum = defaultPrice != null && defaultPrice !== '' ? Number(defaultPrice) : NaN;
  const customNum = customPrice != null && customPrice !== '' ? Number(customPrice) : NaN;
  const hasPct = Number.isFinite(defaultNum) && defaultNum > 0 && Number.isFinite(customNum);
  const pct = hasPct ? ((customNum - defaultNum) / defaultNum) * 100 : null;

  const defaultPriceLine =
    currency && Number.isFinite(defaultNum) ? (
      <span className="secondary-text u-font-size-x-small u-font-weight-400">
        The default price for this check type is{' '}
        <strong>
          {defaultNum.toFixed(2)} {currency}
        </strong>
        .
      </span>
    ) : currency ? (
      <span className="secondary-text u-font-size-x-small u-font-weight-400">
        No default price set for this check type in the pricing table.
      </span>
    ) : (
      <span className="secondary-text u-font-size-x-small u-font-weight-400">
        Billing currency is not set for this company.
      </span>
    );

  const helpText = !isEnterprise
    ? 'Non-enterprise plans are billed per check (one price per check). Per-subcheck or per-item billing only applies to enterprise plans.'
    : null;

  return (
    <div className="d-flex flex-column u-gap-small u-margin-top--medium">
      {defaultPriceLine}
      {hasPct && pct !== 0 && (
        <span className="secondary-text u-font-size-x-small u-font-weight-400 u-margin-top--small">
          Custom price is {Math.abs(pct).toFixed(1)}% <strong>{pct > 0 ? 'above' : 'below'}</strong>{' '}
          the default price.
        </span>
      )}
      {helpText && <FormMessage message={helpText} type="Info" noMargin closeable={false} />}
    </div>
  );
}

export const AdminCompanyCheck = ({
  company,
  checkType,
  companyCheck,
  users,
  billingInfo,
  disableUpdates = false,
  setLoading = () => {},
  onToggle = () => {},
  onSave = () => {},
  onAddCredits = () => {},
}) => {
  // Build initial default values from companyCheck
  const buildFormDefaults = () => {
    const configDefaults = {};
    if (checkType?.configuration) {
      checkType.configuration.forEach(config => {
        if (config.defaultValue !== undefined) {
          configDefaults[config.code] = config.defaultValue;
        }
      });
    }
    return {
      ...companyCheck,
      override_subcheck_billing_default: companyCheck?.override_subcheck_billing_default ?? false,
      bill_per_subcheck: companyCheck?.bill_per_subcheck ?? checkType?.bill_per_subcheck ?? false,
      allow_additional_charges: companyCheck?.allow_additional_charges ?? true,
      bill_per_item: companyCheck?.bill_per_item ?? false,
      configuration: {
        ...configDefaults,
        ...companyCheck?.configuration,
      },
    };
  };

  const {
    control,
    register,
    watch,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: buildFormDefaults(),
    disabled: disableUpdates,
  });

  const [enabled, setEnabled] = useState(!!companyCheck);
  const [showAddCreditsModal, setShowAddCreditsModal] = useState(false);
  const [showDeductCreditsModal, setShowDeductCreditsModal] = useState(false);
  const [credits, setCredits] = useState(companyCheck?.credits);

  // State for hidden field elements - updated only when field count changes to avoid loops
  const [hiddenFieldElements, setHiddenFieldElements] = useState([]);
  const hiddenFieldCountRef = useRef(0);

  // Callback for hidden fields - only update state if count changes to avoid infinite loops
  const handleHiddenFieldsRender = useCallback(elements => {
    const newCount = elements?.length || 0;
    if (newCount !== hiddenFieldCountRef.current) {
      hiddenFieldCountRef.current = newCount;
      setHiddenFieldElements(elements);
    }
  }, []);

  useEffect(() => {
    if (enabled === undefined || (enabled && companyCheck) || (!enabled && !companyCheck)) return;
    onToggle(companyCheck?.id, checkType.slug, enabled);
  }, [enabled]);

  useEffect(() => {
    setEnabled(!!companyCheck);
    reset(buildFormDefaults());
  }, [companyCheck, checkType]);

  const handleSave = async () => {
    // Clear hidden field values before submission
    if (checkType?.configuration?.length > 0) {
      clearHiddenFieldValues(checkType.configuration, getValues, setValue, 'configuration', {
        company: company ?? null,
        companyCheckConfig: companyCheck?.configuration || null,
        candidate: null,
        candidateValues: null,
        checkDetails: null,
        subCheckDetails: null,
        selectedOptionsContext: {},
      });
    }

    const data = getValues();
    await onSave(companyCheck.id, data);
  };

  const handleAddCredits = async data => {
    setShowAddCreditsModal(false);
    data['company_id'] = company.id;
    data['check_type'] = checkType.slug;
    setLoading(true);
    const { data: credits } = await onAddCredits(data);
    setCredits(credits);
    setLoading(false);
  };

  const handleDeductCredits = async data => {
    setLoading(true);
    data = {
      ...data,
      user_name: 'Checkmate Support',
      deduction: true,
      units: data.units,
      company_id: company.id,
      check_type: checkType.slug,
    };
    setShowDeductCreditsModal(false);
    const { data: credits } = await onAddCredits(data);
    setCredits(credits);
    setLoading(false);
  };

  const companyCheckConfig = companyCheck?.configuration || null;
  const candidate = null;
  const checkDetails = null;
  const subCheckDetails = null;
  const candidateValues = null;

  return (
    <ToggleSwitchInput
      name={checkType.slug}
      className="u-margin-right--large"
      text={`Activate ${checkType.title}`}
      subtext={`(${checkType.slug})`}
      toggle={!disableUpdates}
      icon={getCheckTypeIcon(checkType.slug)}
      value={enabled}
      onChange={e => setEnabled(e.target.checked)}>
      <AddCreditsModal
        visible={showAddCreditsModal}
        billingInfo={billingInfo}
        users={users}
        checkType={checkType}
        companyCheck={companyCheck}
        onAdd={handleAddCredits}
        onClose={() => setShowAddCreditsModal(false)}
      />
      <DeductCreditsModal
        visible={showDeductCreditsModal}
        checkType={checkType}
        billingInfo={billingInfo}
        companyCheck={companyCheck}
        onDeduct={handleDeductCredits}
        onClose={() => setShowDeductCreditsModal(false)}
      />

      <h3 className="t-tiny u-uppercase">credits</h3>
      <div className="d-flex justify-content-between">
        <h1 className="u-margin-top--0">{credits}</h1>
        {!disableUpdates && (
          <div>
            <IconButton
              icon="minus"
              tip="Deduct credits"
              onClick={() => setShowDeductCreditsModal(true)}
              className="u-margin-right--small"
            />
            <IconButton
              icon="plus"
              tip="Add credits"
              onClick={() => setShowAddCreditsModal(true)}
            />
          </div>
        )}
      </div>

      <form
        onSubmit={e => {
          e.preventDefault();
          handleSave();
        }}>
        <EditableSettings
          title="Custom pricing"
          open={watch('use_custom_pricing')}
          toggleable
          register={register}
          name="use_custom_pricing"
          className="u-margin-top--large u-margin-bottom">
          <InputGroup
            title={
              billingInfo?.currency
                ? `Please enter a custom price in ${String(billingInfo.currency).toUpperCase()} (company's billing currency).`
                : 'Please enter a custom price.'
            }>
            <Input register={register} name="pricing" type="number" inputProps={{ step: '0.01' }} />
          </InputGroup>
          {watch('use_custom_pricing') && (
            <CustomPricingStatement
              billingCurrency={billingInfo?.currency}
              defaultPrice={companyCheck?.standard_price ?? null}
              customPrice={watch('pricing')}
              isEnterprise={company?.user_type === 'enterprise'}
            />
          )}
        </EditableSettings>

        {checkType?.has_subchecks && (
          <>
            <ToggleSwitch
              register={register}
              name="override_subcheck_billing_default"
              label="Override standard subcheck billing configuration"
              tooltip="When on, you can choose to bill per subcheck or once per check (all inclusive). When off, the check type default is used."
            />
            {watch('override_subcheck_billing_default') && (
              <ToggleSwitch
                register={register}
                name="bill_per_subcheck"
                label="Bill per subcheck"
                tooltip="When on, one line per subcheck. When off, one price for the whole check (all inclusive)."
              />
            )}
          </>
        )}

        {companyCheck?.supports_bill_per_item && (
          <ToggleSwitch
            register={register}
            name="bill_per_item"
            label="Bill per item"
            tooltip="When on, billing is one line per item (e.g. per name checked). When off, one line at the default price."
          />
        )}

        <ToggleSwitch
          register={register}
          name="allow_additional_charges"
          label="Allow additional Charges"
          tooltip="When off, only the base price is used; any additional line item charges for this check type are not applied."
        />

        <ToggleSwitch
          register={register}
          name="has_sensitive_results"
          label="May contain sensitive information"
        />

        <ToggleSwitch
          register={register}
          name="archived"
          tooltip="Prevent check from being initiated. Client will still be able to view launched checks."
          label="Archived"
        />

        {/* Hidden fields: register with form outside ToggleSwitchInput to avoid triggering Collapse */}
        {hiddenFieldElements}

        <FieldConfigRenderer
          fields={checkType?.configuration}
          control={control}
          register={register}
          setValue={setValue}
          getValues={getValues}
          watch={watch}
          formValuesPrefix="configuration"
          errors={errors}
          company={company ?? null}
          companyCheckConfig={companyCheckConfig}
          candidate={candidate}
          checkDetails={checkDetails}
          subCheckDetails={subCheckDetails}
          candidateValues={candidateValues}
          onHiddenFieldsRender={handleHiddenFieldsRender}
        />

        {!disableUpdates && (
          <Button submit className="u-width-100">
            Save
          </Button>
        )}
      </form>
    </ToggleSwitchInput>
  );
};

const AddCreditsModal = ({
  visible,
  users,
  checkType,
  billingInfo,
  companyCheck,
  onAdd = () => {},
  onClose = () => {},
}) => {
  const { register, watch, handleSubmit, reset } = useForm({
    defaultValues: {
      currency: billingInfo?.currency,
      unit_price: companyCheck?.use_custom_pricing
        ? companyCheck.pricing
        : (companyCheck?.standard_price ?? 0),
      units: 0,
    },
  });

  useEffect(() => {
    reset({
      currency: billingInfo?.currency,
      unit_price: companyCheck?.use_custom_pricing
        ? companyCheck.pricing
        : (companyCheck?.standard_price ?? 0),
      units: 0,
    });
  }, [companyCheck, billingInfo]);

  return (
    <AnimatedModal
      title={`Add ${checkType.title} credits`}
      visible={visible}
      onClose={onClose}
      small>
      <form className="u-margin-top" onSubmit={handleSubmit(onAdd)}>
        <InputGroup title="Required by">
          <Select name="user_name" register={register} useDefault value={watch('user_name')}>
            {users.map(user => (
              <Select.Item key={user.id} value={user.name}>
                {user.name} <span className="t-tiny"> ({user.email})</span>
              </Select.Item>
            ))}
          </Select>
        </InputGroup>
        <InputGroup title="Currency">
          <Select name="currency" register={register} value={watch('currency')} useDefault>
            {Object.keys(Currencies).map(currency => (
              <Select.Item key={currency} value={currency}>
                {currency.toUpperCase()}
              </Select.Item>
            ))}
          </Select>
        </InputGroup>
        <InputGroup title="Unit price">
          <Input
            name="unit_price"
            type="number"
            register={register}
            inputProps={{ step: '0.01' }}
          />
        </InputGroup>
        <InputGroup title="Units" useDefault>
          <Input name="units" type="number" register={register} />
        </InputGroup>
        <Button submit className="u-width-100 u-margin-top--large">
          Add
        </Button>
      </form>
    </AnimatedModal>
  );
};

const DeductCreditsModal = ({
  visible,
  checkType,
  billingInfo,
  companyCheck,
  onDeduct = () => {},
  onClose = () => {},
}) => {
  const { register, watch, handleSubmit, reset } = useForm({
    defaultValues: {
      currency: billingInfo?.currency,
      unit_price: companyCheck?.use_custom_pricing
        ? companyCheck.pricing
        : (companyCheck?.standard_price ?? 0),
      units: companyCheck?.credits,
    },
  });

  useEffect(() => {
    reset({
      currency: billingInfo?.currency,
      unit_price: companyCheck?.use_custom_pricing
        ? companyCheck.pricing
        : (companyCheck?.standard_price ?? 0),
      units: companyCheck?.credits,
    });
  }, [companyCheck, billingInfo]);

  return (
    <AnimatedModal
      title={`Deduct ${checkType.title} credits`}
      visible={visible}
      onClose={onClose}
      small>
      <form className="u-margin-top" onSubmit={handleSubmit(onDeduct)}>
        <InputGroup title="Currency">
          <Select name="currency" register={register} value={watch('currency')} useDefault>
            {Object.keys(Currencies).map(currency => (
              <Select.Item key={currency} value={currency}>
                {currency.toUpperCase()}
              </Select.Item>
            ))}
          </Select>
        </InputGroup>
        <InputGroup title="Unit price">
          <Input
            name="unit_price"
            type="number"
            register={register}
            inputProps={{ step: '0.01' }}
          />
        </InputGroup>
        <InputGroup title="Units" useDefault>
          <Input name="units" type="number" max={companyCheck?.credits} register={register} />
        </InputGroup>
        <Button type="delete" submit className="u-width-100 u-margin-top--large">
          Deduct
        </Button>
      </form>
    </AnimatedModal>
  );
};
